import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { dateTime, money } from "@/lib/format";
import { Alert, Badge, Card, PageHeader, Skeleton, Stat, Table, cx } from "@/components/ui";

/**
 * Is money arriving, and if not, why.
 *
 * Two halves on one screen because that is the shape of the question. The
 * gateway's configuration and the payments it produced answer it together: a
 * correctly configured gateway with nothing settled and a pile of pending
 * payments is a different problem from a gateway missing its passphrase, and
 * neither half tells you which on its own.
 *
 * Until this existed, both could only be read with curl.
 */

interface Gateway {
  gateway: string;
  live: boolean;
  callback_url: string;
  missing_settings: string[] | null;
  payment_id_field: string;
  source_ips: { enforced: boolean; allowed: string[] | null };
  confirms_with_provider: boolean;
  rejected_count: number;
  rejected_callbacks: Array<Record<string, unknown>> | null;
}

interface Payment {
  id: string;
  account_id: string;
  account_name: string;
  amount_cents: number;
  status: string;
  provider: string;
  provider_ref?: string;
  method?: string;
  failure_reason?: string;
  created_at: string;
  settled_at?: string | null;
}

interface Totals {
  settled_cents: number;
  settled_count: number;
  pending_cents: number;
  pending_count: number;
  failed_count: number;
}

const FILTERS = ["", "pending", "succeeded", "failed", "refunded"] as const;

function tone(status: string) {
  if (status === "succeeded") return "good" as const;
  if (status === "failed") return "bad" as const;
  return "progress" as const;
}

export function PlatformPayments() {
  const [status, setStatus] = useState<(typeof FILTERS)[number]>("");

  const { data: gateway } = useQuery({
    queryKey: ["payments-gateway"],
    queryFn: () => api.get<Gateway>("/v1/admin/payments/gateway"),
  });

  const { data, isLoading } = useQuery({
    queryKey: ["platform-payments", status],
    queryFn: () =>
      api.get<{ data: Payment[]; totals: Totals; window: string }>(
        `/v1/admin/payments${status ? `?status=${status}` : ""}`,
      ),
    // Money moves while somebody is looking at this screen.
    refetchInterval: 30_000,
  });

  const missing = gateway?.missing_settings ?? [];

  return (
    <>
      <PageHeader title="Payments" subtitle="What has been taken, and whether the gateway can take it." />

      {/* First, because nothing below it means anything if this is wrong: a
          missing setting means callbacks cannot be verified, which means no
          wallet is ever credited no matter how many payments start. */}
      {missing.length > 0 && (
        <div className="mb-4">
          <Alert>
            The payment gateway is missing {missing.join(", ")}. Callbacks cannot be verified, so no
            wallet will be credited until this is set.
          </Alert>
        </div>
      )}

      {gateway && !gateway.live && (
        <div className="mb-4">
          <Alert tone="warn">
            Running the in-process sandbox, not {gateway.gateway === "sandbox" ? "a real gateway" : gateway.gateway}.
            No real money moves. Set the gateway credentials to go live.
          </Alert>
        </div>
      )}

      {data?.totals && (
        <div className="mb-5 grid gap-4 sm:grid-cols-3">
          <Stat
            label="Settled"
            value={money(data.totals.settled_cents)}
            hint={`${data.totals.settled_count} in ${data.window}`}
          />
          <Stat
            label="Still pending"
            value={money(data.totals.pending_cents)}
            hint={
              data.totals.pending_count === 0
                ? "Nothing waiting"
                : `${data.totals.pending_count} started and not finished`
            }
          />
          <Stat label="Failed" value={String(data.totals.failed_count)} hint={data.window} />
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map((option) => (
          <button
            key={option || "all"}
            onClick={() => setStatus(option)}
            className={cx(
              "rounded-lg px-3 py-1.5 text-sm font-medium",
              status === option ? "bg-ink-900 text-white" : "bg-white text-ink-600 hover:bg-ink-100",
            )}
          >
            {option === "" ? "All" : option}
          </button>
        ))}
      </div>

      {isLoading ? (
        <Card>
          <Skeleton className="h-48 w-full" />
        </Card>
      ) : (data?.data.length ?? 0) === 0 ? (
        <Card>
          <p className="text-sm text-ink-500">
            {status ? `No ${status} payments.` : "No payments yet."}
          </p>
        </Card>
      ) : (
        <Table head={["When", "Account", "Amount", "Status", "Provider reference"]}>
          {data?.data.map((payment) => (
            <tr key={payment.id}>
              <td className="ws-td whitespace-nowrap text-ink-500">{dateTime(payment.created_at)}</td>
              <td className="ws-td">
                <span className="font-medium">{payment.account_name || payment.account_id}</span>
                <p className="mt-0.5 font-mono text-xs text-ink-400">{payment.account_id}</p>
              </td>
              <td className="ws-td font-semibold">{money(payment.amount_cents)}</td>
              <td className="ws-td">
                <Badge tone={tone(payment.status)}>{payment.status}</Badge>
                {payment.failure_reason && (
                  <p className="mt-1 text-xs text-ink-400">{payment.failure_reason}</p>
                )}
              </td>
              <td className="ws-td">
                <span className="font-mono text-xs text-ink-500">{payment.provider_ref || "—"}</span>
                <p className="mt-0.5 text-xs text-ink-400">
                  {payment.provider}
                  {payment.method ? ` · ${payment.method}` : ""}
                </p>
              </td>
            </tr>
          ))}
        </Table>
      )}

      {/* The diagnostics the API already kept and nothing displayed. A refused
          callback is the failure that looks like nothing happening: the provider
          believes it paid, the wallet was never credited, and the only evidence
          is here. */}
      {gateway && (
        <Card className="mt-5">
          <h2 className="text-lg font-semibold tracking-tight">Gateway</h2>
          <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
            <Row label="Provider" value={gateway.gateway} />
            <Row label="Mode" value={gateway.live ? "Live" : "Sandbox"} />
            <Row label="Callback URL" value={gateway.callback_url} mono />
            <Row label="Payment id field" value={gateway.payment_id_field} mono />
            <Row
              label="Source IP check"
              value={
                gateway.source_ips.enforced
                  ? `Enforced: ${(gateway.source_ips.allowed ?? []).join(", ")}`
                  : "Off — any address may post a callback"
              }
            />
            <Row
              label="Confirms with provider"
              value={gateway.confirms_with_provider ? "Yes" : "No — signature only"}
            />
          </dl>

          {gateway.rejected_count > 0 && (
            <div className="mt-4">
              <p className="text-sm font-semibold text-ink-900">
                {gateway.rejected_count} refused callback{gateway.rejected_count === 1 ? "" : "s"}
              </p>
              <p className="mt-1 text-sm text-ink-500">
                Each of these is a payment the provider thinks it made and this platform would not
                accept. Shapes only — no bodies and no credentials.
              </p>
              <pre className="mt-3 overflow-x-auto rounded-xl bg-ink-900 p-4 text-xs leading-relaxed text-ink-200">
                {JSON.stringify(gateway.rejected_callbacks, null, 2)}
              </pre>
            </div>
          )}
        </Card>
      )}
    </>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-ink-400">{label}</dt>
      <dd className={cx("mt-0.5 break-all text-ink-800", mono && "font-mono text-xs")}>{value}</dd>
    </div>
  );
}
